import { ParsedUrlQuery } from 'querystring'
import locales from '@/constants/locales'
import { getItemOrFirstEntry } from '@/utils/array-utils'

/**
 * If we ever are able to get @/constants/locales to be a ts file we should
 * build this type from the typeof locales['all'][number]
 */
export type Locale =
  | 'en'
  | 'es'
  | 'pt-BR'
  | 'de-DE'
  | 'fr'
  | 'zh-CN'
  | 'it'
  | 'pl'
  | 'zh-Hant'
  | 'ach-UG'
  | 'es-ES'
  | 'no'
  | 'fi'
  | 'sv'
  | 'nl'

const ALL_LOCALES_LOWERCASE = {} as Record<string, Locale>

locales.all.forEach((lng) => (ALL_LOCALES_LOWERCASE[lng.toLowerCase()] = lng))

/**
 * This is useful in getStaticProps but shouldn't be used elsewhere
 * because rewrites will have the params of the path you rewrite to,
 * not the params of the URL in your URL bar
 * @param params
 */
export const getLocaleFromParams = (params: ParsedUrlQuery | undefined): Locale => {
  return getLocaleOrDefault(params?.locale)
}

export function getLocaleFromPath(path: string): Locale {
  if (isFirstPathParamLocale(path)) {
    const potentialLocale = getPotentialLocaleFromPath(path)
    return ALL_LOCALES_LOWERCASE[potentialLocale.toLowerCase()] || (locales.defaultLocale as Locale)
  } else {
    return locales.defaultLocale as Locale
  }
}

/**
 * Don't use this for rewrites because it doesn't have the /en path
 * that is needed for rewrites!
 * @param path
 */
export function getLocaleSectionForRedirectPath(path: string): string {
  const locale = getLocaleFromPath(path)
  return locale === locales.defaultLocale ? '' : `/${locale}`
}

export function getPotentialLocaleFromPath(path: string): string {
  const split = path?.split(/[/?]/)
  return split?.[1] || ''
}

export function isFirstPathParamLocale(path: string) {
  const pathLocale = getPotentialLocaleFromPath(path)
  return !!ALL_LOCALES_LOWERCASE[pathLocale.toLowerCase()]
}

export function getLocaleOrDefault(locale: string | string[] | undefined): Locale {
  const localeFromQuery = getItemOrFirstEntry(locale)
  if (localeFromQuery) {
    return (ALL_LOCALES_LOWERCASE[localeFromQuery?.toLowerCase()] ? localeFromQuery : locales.defaultLocale) as Locale
  } else {
    return locales.defaultLocale as Locale
  }
}
